import { addDays, DayKey, maxDay, minDay, mondayOf, monthStart } from "./timeline-dates";

/**
 * How a bar is drawn in its entry's color: `tint` (light fill, colored border), `hatched` (striped, e.g. planned)
 * or `solid` (filled, white text).
 */
export type TimelineBarStyle = "tint" | "hatched" | "solid";

/** Red triangle for a date mismatch, yellow for a date warning. */
export type TimelineWarnLevel = "mismatch" | "warning";

/** What one Gantt column stands for in the header: a day, or a week / month band over its days. */
export type TimelineScale = "days" | "weeks" | "months";

/** One-day entries on the Gantt: `chip` (dot, label and context) or `line` (a line on the day with a small pill). */
export type TimelineDateStyle = "chip" | "line";

/** The today line on the Gantt's week and month scales. */
export type TimelineTodayStyle = "solid" | "dashed" | "dotted";

/** Gantt row labels: `two-line` (main text over sub text) or `one-line` (sub text after the main text). */
export type TimelineLabelDensity = "two-line" | "one-line";

/** One item on a timeline. A data set's mapper builds these; the views only draw them. */
export interface TimelineEntry {
  id: string;
  rowId: string;
  /** The data set's own kind of entry (e.g. "spud", "build"). The views don't read it. */
  type: string;
  /** Show/hide checkbox key; entries without one are always shown. */
  category: string | null;
  label: string;
  /** Shorter text where room is tight, e.g. "Ful" or a build's serial; the label is used when unset. */
  shortLabel?: string;
  /** Bars: shorter versions of the label, longest first; a bar too narrow for its label shows the first that fits. */
  shorterLabels?: string[];
  /**
   * One-day entries on the week and month scales: longer text used in place of the short label when it fits before the
   * row's next entry, e.g. a build step's name after the build number.
   */
  roomLabel?: string;
  /** Second line on a chip. */
  context: string;
  /** Full description for the tooltip and the accessible name. */
  description: string;
  start: DayKey;
  end: DayKey;
  /** Dot color for a chip, accent color for a bar. */
  color: string;
  /** Chip order inside one day (lower first). */
  order: number;
  /** Tinted chip background in `color` instead of the neutral one. */
  tint?: boolean;
  /** Drawn as a bar across start–end instead of a chip on `start`. */
  bar: boolean;
  barStyle?: TimelineBarStyle;
  /** Red edge on a bar. */
  late?: boolean;
  /** Triangle: this entry's own date is part of a mismatch (red) or a warning (yellow). */
  warn: TimelineWarnLevel | null;
  /** Has no date of its own and borrows another entry's date or today; a view fitted to saved dates can leave it out. */
  undated?: boolean;
}

export interface TimelineRow {
  id: string;
  label: string;
  /** Shown smaller after the label, e.g. a job's site. */
  labelSuffix?: string;
  sub: string;
  /** Group heading row (e.g. a project) above its item rows. */
  isGroup: boolean;
  /** Values the filter defs match against, e.g. { customer, project }. */
  facets: Record<string, string>;
  /** Display text for each facet value, for the filter options. */
  facetLabels: Record<string, string>;
  /** Selecting the row label opens this detail (e.g. a project or job overview). */
  detailId?: string;
  /** Roll-up warning icon on the row label; selecting it opens this detail. */
  warning: { detailId: string; label: string; level: TimelineWarnLevel } | null;
}

export interface TimelineFilterDef {
  key: string;
  label: string;
  allLabel: string;
  /** Narrows this filter's options by the value chosen in that filter. */
  dependsOn?: string;
  /** Groups the options under this facet's label, e.g. projects under their customer. */
  groupBy?: string;
  /** Selected at first and by Reset filters; "all" when not set. */
  defaultValue?: string;
  /** Extra options listed right after the "all" option; each matches rows whose `facet` equals `facetValue`. */
  presets?: TimelineFilterPreset[];
}

export interface TimelineFilterPreset {
  value: string;
  label: string;
  facet: string;
  facetValue: string;
  /** A group row only shows when one of its shown item rows has this facet value (e.g. an active job). */
  requireItem?: { facet: string; facetValue: string };
}

export interface TimelineFilterOption {
  value: string;
  label: string;
  facets: Record<string, string>;
}

export interface TimelineCategoryDef {
  key: string;
  label: string;
  color: string;
}

/** What every detail needs for the Details card header; data sets extend it with their own content. */
export interface TimelineDetailBase {
  id: string;
  kind: string;
  title: string;
  sub: string;
}

export interface TimelineData<TDetail extends TimelineDetailBase = TimelineDetailBase> {
  rows: TimelineRow[];
  entries: TimelineEntry[];
  details: Map<string, TDetail>;
}

/** The red used for mismatch triangles and late items. */
export const TIMELINE_WARN_COLOR = "#b3261e";

/** The amber used for date warning triangles. */
export const TIMELINE_CAUTION_COLOR = "#b26a00";

export interface TimelineRangeConfig {
  /** Full weeks loaded before this week. */
  weeksBack: number;
  /** Full weeks loaded after this week. */
  weeksAhead: number;
  /** Days the Gantt shows at once. */
  windowDays: number;
}

/** 13 weeks (last week, this week, 11 ahead), 4 of them on screen at a time. */
export const DEFAULT_TIMELINE_RANGE: TimelineRangeConfig = { weeksBack: 1, weeksAhead: 11, windowDays: 28 };

export interface TimelineRange {
  /** First and last loaded day. Days outside are grayed out on the calendar. */
  start: DayKey;
  end: DayKey;
  /** Window start (a Monday) for today. */
  todayWindowStart: DayKey;
  firstWindowStart: DayKey;
  lastWindowStart: DayKey;
  windowDays: number;
}

/**
 * From `weeksBack` weeks before this week's Monday (or the 1st of this month, if earlier) to the Sunday `weeksAhead`
 * weeks after this week. The Gantt pages a week at a time inside it (not at all when one window covers it); the
 * Calendar pages through months that overlap it.
 */
export function timelineRange(today: DayKey, config: TimelineRangeConfig = DEFAULT_TIMELINE_RANGE): TimelineRange {
  const thisMonday = mondayOf(today);
  const firstWindowStart = addDays(thisMonday, -7 * config.weeksBack);
  const end = addDays(thisMonday, 7 * (config.weeksAhead + 1) - 1);
  return {
    start: minDay(monthStart(today), firstWindowStart),
    end,
    todayWindowStart: thisMonday,
    firstWindowStart,
    lastWindowStart: maxDay(firstWindowStart, addDays(end, 1 - config.windowDays)),
    windowDays: config.windowDays,
  };
}

/** The latest start for a window of `days` days that still ends inside the range. */
export function lastWindowStartFor(range: TimelineRange, days: number): DayKey {
  return maxDay(range.firstWindowStart, addDays(range.end, 1 - days));
}

export function clampWindowStart(range: TimelineRange, start: DayKey, days = range.windowDays): DayKey {
  return maxDay(range.firstWindowStart, minDay(lastWindowStartFor(range, days), start));
}
